import type { OrganizationUser } from "@cohesive/api-client";
import {
  BlockStateSourceError,
  type BlockStateEntry,
  type BlockStateOp,
  type BlockStatePerson,
  type BlockStateSnapshot,
  type BlockStateSource,
  type BlockStateSourceEvent,
  type BlockStateViewer,
} from "@cohesive/ui-components";
import createDebug from "debug";
import { useEffect, useMemo } from "react";
import { blockStateHub, type HubMember } from "./blockStateHub";
import { blockStateTokens } from "./blockStateTokens";
import {
  fromWire,
  isUnavailable,
  readBlockState,
  type WireRow,
} from "./blockStateWorker";
import { useUserInfo } from "./user";
import { useOrgUsers } from "./users";

/**
 * Block state (`cohesive.state`) on the dashboard side: one mirror per file,
 * shared by every surface showing it (canvas card, lightbox, detail pane).
 * While held it is subscribed through the tab's hub, which carries its
 * rows, writes and live changes; the capture page reads once over HTTPS.
 */

const debug = createDebug("cohesive:file-record-state");

/** Org members by id, for naming the authors the server records. */
export type BlockStatePeople = ReadonlyMap<string, BlockStatePerson>;

function nameAuthors(
  entry: BlockStateEntry,
  people: BlockStatePeople,
): BlockStateEntry {
  const name = (author: BlockStatePerson | null | undefined) =>
    author?.id ? (people.get(author.id) ?? author) : null;
  return {
    ...entry,
    createdBy: name(entry.createdBy),
    updatedBy: name(entry.updatedBy),
  };
}

export class FileRecordStateStore implements HubMember {
  private entries = new Map<string, BlockStateEntry>();
  private loaded = false;
  private loading: Promise<void> | null = null;
  private unavailableNow = false;
  private listeners = new Set<(event: BlockStateSourceEvent) => void>();

  constructor(
    readonly id: string,
    private viewer: () => BlockStateViewer | null,
    private people: () => BlockStatePeople = () => new Map(),
  ) {}

  getSnapshot(): BlockStateSnapshot | null {
    if (!this.loaded) return null;
    const people = this.people();
    return {
      entries: [...this.entries.values()]
        .filter((e) => !e.deleted)
        .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
        .map((e) => nameAuthors(e, people)),
      viewer: this.viewer(),
      readonly: blockStateTokens.peek(this.id)?.write === false,
    };
  }

  isUnavailable(): boolean {
    return this.unavailableNow;
  }

  subscribe(listener: (event: BlockStateSourceEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** The capture page's one read; everywhere else rows come from the hub. */
  ensureLoaded(): void {
    if (this.loaded || this.loading || !blockStateHub.disabled) return;
    this.loading = readBlockState(this.id)
      .then(({ version, rows }) => this.snapshot(version, rows))
      .catch((err) => {
        debug("load %s failed: %o", this.id, err);
        if (isUnavailable(err)) this.unavailable();
      })
      .finally(() => {
        this.loading = null;
      });
  }

  // ── Hub ──

  /** Replaces the mirror, except rows newer than the snapshot: a change can
   *  overtake the snapshot of a re-subscribe. */
  snapshot(version: number, rows: WireRow[]): void {
    const entries = new Map(rows.map((r) => [r.key, fromWire(r)]));
    for (const [key, e] of this.entries) {
      if (e.version > version) entries.set(key, e);
    }
    this.entries = entries;
    this.loaded = true;
    this.setUnavailable(false);
    this.emitReset();
  }

  change(rows: WireRow[]): void {
    this.applyChanges(rows.map(fromWire));
  }

  unavailable(): void {
    this.setUnavailable(true);
  }

  /** Fold rows into the mirror, keeping only ones newer than what it holds,
   *  which also drops the echo of this tab's own writes. A delete is kept as
   *  a tombstone so an older write cannot bring the key back. */
  applyChanges(changes: readonly BlockStateEntry[], writer?: object): void {
    const accepted: BlockStateEntry[] = [];
    for (const change of changes) {
      const have = this.entries.get(change.key);
      if (have && have.version >= change.version) continue;
      this.entries.set(change.key, change);
      if (change.deleted && (!have || have.deleted)) continue;
      accepted.push(change);
    }
    if (accepted.length > 0) {
      const people = this.people();
      this.emit({
        type: "change",
        changes: accepted.map((e) => nameAuthors(e, people)),
        writer,
      });
    }
  }

  async submit(
    ops: readonly BlockStateOp[],
    writer?: object,
  ): Promise<BlockStateEntry[]> {
    const rows = (await blockStateHub.write(this.id, ops)).map(fromWire);
    this.applyChanges(rows, writer);
    const people = this.people();
    return rows.map((e) => nameAuthors(e, people));
  }

  emitReset(): void {
    const snapshot = this.getSnapshot();
    if (snapshot) this.emit({ type: "reset", snapshot });
  }

  private setUnavailable(unavailable: boolean): void {
    if (this.unavailableNow === unavailable) return;
    this.unavailableNow = unavailable;
    this.emit({ type: "unavailable", unavailable });
  }

  private emit(event: BlockStateSourceEvent): void {
    for (const listener of [...this.listeners]) listener(event);
  }
}

// ── Registry ────────────────────────────────────────────────────────────────

const stores = new Map<string, { store: FileRecordStateStore; refs: number }>();
let currentViewer: BlockStateViewer | null = null;
let currentPeople: BlockStatePeople = new Map();

export function acquireFileRecordState(fileRecordId: string): {
  store: FileRecordStateStore;
  release: () => void;
} {
  let slot = stores.get(fileRecordId);
  if (!slot) {
    slot = {
      store: new FileRecordStateStore(
        fileRecordId,
        () => currentViewer,
        () => currentPeople,
      ),
      refs: 0,
    };
    stores.set(fileRecordId, slot);
    blockStateHub.add(slot.store);
  }
  slot.refs += 1;
  const held = slot;
  let released = false;
  return {
    store: held.store,
    release: () => {
      if (released) return;
      released = true;
      held.refs -= 1;
      if (held.refs === 0 && stores.get(fileRecordId) === held) {
        stores.delete(fileRecordId);
        blockStateHub.remove(fileRecordId);
      }
    },
  };
}

/** The capture page reads each block once and opens no socket. */
export function readBlockStateOnce(): void {
  blockStateHub.disabled = true;
}

let announcedPeople: BlockStatePeople = currentPeople;

// One map per fetched list, so every card holding it agrees it is current.
const peopleByList = new WeakMap<OrganizationUser[], BlockStatePeople>();

function peopleFromOrgUsers(users: OrganizationUser[]): BlockStatePeople {
  let people = peopleByList.get(users);
  if (!people) {
    people = new Map(
      users.map((u) => [
        u.id,
        {
          id: u.id,
          name: u.name || [u.first_name, u.last_name].join(" ").trim(),
          avatarUrl: u.picture_url || null,
        },
      ]),
    );
    peopleByList.set(users, people);
  }
  return people;
}

/** Tell open blocks the member list changed (after render). A different
 *  viewer remounts the frame (it is in the prepare salt). */
function announceFileRecordStatePeople(people: BlockStatePeople): void {
  if (announcedPeople === people) return;
  announcedPeople = people;
  for (const { store } of stores.values()) store.emitReset();
}

// ── Hook ────────────────────────────────────────────────────────────────────

/** A source that holds nothing until a block's card subscribes to it. */
export class LazyFileRecordState implements BlockStateSource {
  private held: { store: FileRecordStateStore; release: () => void } | null =
    null;
  private holds = 0;

  constructor(
    readonly id: string,
    private viewer: () => BlockStateViewer | null,
  ) {}

  getSnapshot(): BlockStateSnapshot | null {
    return this.held?.store.getSnapshot() ?? null;
  }

  getBoot(): { viewer: BlockStateViewer | null; readonly: boolean } {
    return {
      viewer: this.viewer(),
      readonly: blockStateTokens.peek(this.id)?.write === false,
    };
  }

  isUnavailable(): boolean {
    return this.held?.store.isUnavailable() ?? false;
  }

  subscribe(listener: (event: BlockStateSourceEvent) => void): () => void {
    const store = this.hold();
    const unsubscribe = store.subscribe(listener);
    store.ensureLoaded();
    return this.once(() => {
      unsubscribe();
      this.unhold();
    });
  }

  submit(
    ops: readonly BlockStateOp[],
    writer?: object,
  ): Promise<BlockStateEntry[]> {
    if (this.held) return this.held.store.submit(ops, writer);
    return Promise.reject(
      new BlockStateSourceError("failed", "The write was not saved."),
    );
  }

  private once(fn: () => void): () => void {
    let done = false;
    return () => {
      if (done) return;
      done = true;
      fn();
    };
  }

  private hold(): FileRecordStateStore {
    this.holds += 1;
    this.held ??= acquireFileRecordState(this.id);
    return this.held.store;
  }

  private unhold(): void {
    this.holds -= 1;
    if (this.holds === 0) {
      this.held?.release();
      this.held = null;
    }
  }
}

/**
 * The live `cohesive.state` source for an HTML block's file. Pass the real
 * file's id — a link row's `target_file_record_id` — so every surface
 * showing the file shares one mirror (the server resolves links either way).
 * Nothing loads until the block subscribes.
 */
export function useFileRecordState(
  fileRecordId: string | null | undefined,
): BlockStateSource | undefined {
  const { data: me } = useUserInfo();

  const user = me?.user;
  const viewer = useMemo<BlockStateViewer | null>(
    () =>
      user
        ? {
            id: user.id,
            name:
              user.name || [user.first_name, user.last_name].join(" ").trim(),
            avatarUrl: user.picture_url || null,
          }
        : null,
    [user],
  );
  const { data: users } = useOrgUsers({ enabled: !!fileRecordId && !!user });
  const people = users ? peopleFromOrgUsers(users) : currentPeople;
  // Read during render (the card bakes the viewer into the block document),
  // so set here; running blocks hear of a new member list after commit.
  if (viewer) currentViewer = viewer;
  currentPeople = people;
  useEffect(() => {
    announceFileRecordStatePeople(people);
  }, [people]);

  return useMemo(
    () =>
      fileRecordId
        ? new LazyFileRecordState(fileRecordId, () => currentViewer)
        : undefined,
    [fileRecordId],
  );
}
